 function _nullishCoalesce(lhs, rhsFn) { if (lhs != null) { return lhs; } else { return rhsFn(); } } function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }import graphql from 'babel-plugin-relay/macro'
import React, {useMemo, useState} from 'react'
import { useFragment, usePreloadedQuery} from 'react-relay'
import {EmptyDropdownMenuItemLabel} from '~/components/EmptyDropdownMenuItemLabel'
import {SearchMenuItem} from '~/components/SearchMenuItem'
import useEventCallback from '~/hooks/useEventCallback'
import useModal from '~/hooks/useModal'
import useSearchFilter from '~/hooks/useSearchFilter'
import {useQueryParameterParser} from '~/utils/useQueryParameterParser'

import DropdownMenuLabel from '../../../../components/DropdownMenuLabel'
import Menu from '../../../../components/Menu'
import MenuItem from '../../../../components/MenuItem'
import useAtmosphere from '../../../../hooks/useAtmosphere'

import useMutationProps from '../../../../hooks/useMutationProps'
import ChangeTaskTeamMutation from '../../../../mutations/ChangeTaskTeamMutation'


import TaskFooterTeamAssigneeAddIntegrationDialog from './TaskFooterTeamAssigneeAddIntegrationDialog'

const query = graphql`
  query TaskFooterTeamAssigneeMenu_viewerIntegrationsQuery($teamId: ID!) {
    viewer {
      id
      teamMember(teamId: $teamId) {
        id
        integrations {
          id
          atlassian {
            isActive
          }
          github {
            isActive
          }
        }
      }
    }
  }
`







const gqlQuery = graphql`
  query TaskFooterTeamAssigneeMenuQuery {
    viewer {
      id
      teams {
        id
        name
        teamMembers(sortBy: "preferredName") {
          userId
          preferredName
        }
      }
    }
  }
`

const TaskFooterTeamAssigneeMenu = (props) => {
  const {menuProps, task: taskRef, queryRef} = props
  const data = usePreloadedQuery(gqlQuery, queryRef)
  const {viewer} = data

  const {closePortal: closeTeamAssigneeMenu} = menuProps
  const {userIds, teamIds} = useQueryParameterParser(viewer.id)

  const task = useFragment(
    graphql`
      fragment TaskFooterTeamAssigneeMenu_task on Task {
        id
        team {
          id
        }
        integration {
          __typename
        }
      }
    `,
    taskRef
  )
  const {team, id: taskId, integration} = task
  const isGitHubTask = _optionalChain([integration, 'optionalAccess', _ => _.__typename]) === '_xGitHubIssue'
  const isJiraTask = _optionalChain([integration, 'optionalAccess', _2 => _2.__typename]) === 'JiraIssue'

  const {id: teamId} = team
  const {teams} = viewer
  const assignableTeams = useMemo(() => {
    if (userIds) {
      return teams.filter(
        ({teamMembers}) => !!teamMembers.find(({userId}) => userIds.includes(userId))
      )
    }
    if (teamIds) {
      return teams.filter(({id}) => teamIds.includes(id))
    }
    return teams
  }, [teamIds, userIds])
  const taskTeamIdx = useMemo(
    () => assignableTeams.findIndex(({id}) => id === teamId) + 1,
    [teamId, assignableTeams]
  )

  const atmosphere = useAtmosphere()
  const {submitting, submitMutation, onError, onCompleted} = useMutationProps()

  const onDialogClose = useEventCallback(() => {
    closeTeamAssigneeMenu()
  })
  const {
    modalPortal: addIntegrationModalPortal,
    openPortal: openAddIntegrationPortal,
    closePortal: closeAddIntegrationPortal
  } = useModal({
    onClose: onDialogClose,
    id: 'taskFooterTeamAssigneeAddIntegration'
  })
  const [newTeam, setNewTeam] = useState({id: '', name: ''})

  const handleAddIntegrationConfirmed = () => {
    closeAddIntegrationPortal()
    if (!newTeam.id) return

    submitMutation()
    ChangeTaskTeamMutation(atmosphere, {taskId, teamId: newTeam.id}, {onError, onCompleted})
    setNewTeam({id: '', name: ''})
    closeTeamAssigneeMenu()
  }
  const handleClose = () => {
    closeAddIntegrationPortal()
    closeTeamAssigneeMenu()
  }

  const handleTaskUpdate = (nextTeam) => async () => {
    if (!submitting && teamId !== nextTeam.id) {
      if (isGitHubTask || isJiraTask) {
        const result =
          await atmosphere.fetchQuery(query, {
            teamId: nextTeam.id
          })
        const {github, atlassian} = _nullishCoalesce(_optionalChain([result, 'optionalAccess', _3 => _3.viewer, 'optionalAccess', _4 => _4.teamMember, 'optionalAccess', _5 => _5.integrations]), () => ( {}))

        if ((isGitHubTask && !_optionalChain([github, 'optionalAccess', _6 => _6.isActive])) || (isJiraTask && !_optionalChain([atlassian, 'optionalAccess', _7 => _7.isActive]))) {
          // viewer is not integrated, now we have these options:
          // 1) if user has integration in source team, then we will use that, but still ask
          // 2) if accessUser is someone else and they have integration for target team, then we will use that without asking
          // 3) else we need to ask the user to integrate with complete oauth flow
          // For now ignore 2) and 3) and just assume it's 1) as that's the most common case.
          setNewTeam(nextTeam)
          openAddIntegrationPortal()
          return
        }
      }
      submitMutation()
      ChangeTaskTeamMutation(atmosphere, {taskId, teamId: nextTeam.id}, {onError, onCompleted})
      closeTeamAssigneeMenu()
    }
  }

  const {
    query: searchQuery,
    filteredItems: matchedAssignableTeams,
    onQueryChange
  } = useSearchFilter(assignableTeams, (team) => team.name)

  return (
    React.createElement(Menu, {
      keepParentFocus: true,
      ...menuProps,
      defaultActiveIdx: taskTeamIdx,
      ariaLabel: 'Assign this task to another team',}

      , React.createElement(DropdownMenuLabel, null, "Move to:" )
      , assignableTeams.length > 5 && (
        React.createElement(SearchMenuItem, { placeholder: "Search teams" , onChange: onQueryChange, value: searchQuery,} )
      )
      , query && matchedAssignableTeams.length === 0 && (
        React.createElement(EmptyDropdownMenuItemLabel, { key: "no-results",}, "No teams found!"  )
      )
      , matchedAssignableTeams.map((team) => {
        return (
          React.createElement(MenuItem, {
            key: team.id,
            label: team.name,
            onClick: handleTaskUpdate(team),
            noCloseOnClick: true,}
          )
        )
      })
      , (isGitHubTask || isJiraTask) &&
        addIntegrationModalPortal(
          React.createElement(TaskFooterTeamAssigneeAddIntegrationDialog, {
            onClose: handleClose,
            onConfirm: handleAddIntegrationConfirmed,
            serviceName: isGitHubTask ? 'GitHub' : 'Jira',
            teamName: newTeam.name,}
          )
        )
    )
  )
}

export default TaskFooterTeamAssigneeMenu
